"use client";

/**
 * Root global-error.tsx · the last line of defence.
 *
 * Next.js falls back to this boundary when:
 *   - the root layout itself throws
 *   - a route-segment error.tsx throws while rendering its own UI
 *   - hydration errors occur above any route-level boundary
 *
 * Without this file, Next shows the bare browser-default text
 * "Application error: a client-side exception has occurred (see the
 * browser console for more information)" — which is what Shrey was
 * seeing on /fy2627 and /bd/dashboard when a downstream client
 * component crashed AND the segment boundary also failed.
 *
 * Must render its own <html> + <body> because it replaces the root
 * layout. Praxeti-styled, with a Retry + Home action and the actual
 * error message + digest surfaced (sanitised) so we can debug from
 * the field.
 */

import { useEffect } from "react";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.error("[global-error] caught:", error);
      console.error("[global-error] message:", error?.message);
      console.error("[global-error] stack:", error?.stack);
      console.error("[global-error] digest:", error?.digest);
    }
  }, [error]);

  const safeMessage = (() => {
    try {
      const m = error?.message?.toString?.() ?? "";
      if (!m) return "";
      return m.split("\n")[0].slice(0, 240);
    } catch {
      return "";
    }
  })();

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          background:
            "linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%)",
          fontFamily:
            "'Jost', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
          color: "#0A0A0A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 560,
            width: "100%",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 18,
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 18,
              background: "rgba(217,96,96,0.12)",
              border: "0.5px solid rgba(217,96,96,0.28)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 30,
            }}
            aria-hidden="true"
          >
            ⚠️
          </div>

          <h1
            style={{
              fontFamily: "'Jost', Georgia, serif",
              fontSize: "1.85rem",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              margin: 0,
            }}
          >
            Something went sideways.
          </h1>

          <p
            style={{
              fontSize: "0.92rem",
              color: "#475160",
              maxWidth: 480,
              lineHeight: 1.55,
              margin: 0,
            }}
          >
            A render error escaped the page-level guardrails before we
            could catch it cleanly. Your data is safe — nothing was
            saved or edited. Retry, or jump back to the command centre.
          </p>

          {(safeMessage || error?.digest) && (
            <div
              style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: "0.74rem",
                color: "#7A1F1F",
                background: "rgba(217,96,96,0.06)",
                padding: "10px 14px",
                borderRadius: 8,
                border: "0.5px solid rgba(217,96,96,0.20)",
                maxWidth: 560,
                textAlign: "left",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {safeMessage}
              {error?.digest && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: "0.68rem",
                    color: "#475160",
                  }}
                >
                  ref · {error.digest}
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
            <button
              onClick={() => {
                try {
                  reset();
                } catch {
                  if (typeof window !== "undefined") window.location.reload();
                }
              }}
              style={{
                background: "linear-gradient(135deg, #00C853 0%, #00A040 100%)",
                color: "#FFFFFF",
                fontFamily: "'Jost', Georgia, serif",
                fontWeight: 600,
                fontSize: "0.88rem",
                padding: "11px 22px",
                borderRadius: 999,
                border: "0.5px solid rgba(0,200,83,0.40)",
                boxShadow: "0 4px 14px rgba(0,200,83,0.28)",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            <a
              href="/founder"
              style={{
                background: "rgba(255,255,255,0.92)",
                color: "#0A0A0A",
                fontFamily: "'Jost', Georgia, serif",
                fontWeight: 600,
                fontSize: "0.88rem",
                padding: "11px 22px",
                borderRadius: 999,
                border: "0.5px solid rgba(0,0,0,0.10)",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Command centre
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
